// ---------------------------------------------------------------------------
// Format helpers — shared across V11 feature surfaces
// ---------------------------------------------------------------------------
// Consolidates copies of compact number formatting that previously lived
// inline in FeedCard, FeedKpiStrip, OverlapVenn, OverlapStatCards,
// AccountHealthZone, OverlapAudienceProfile, OverviewPanel, TrendsPulseBoard,
// ComparisonMetricsCard. Import from here.
// ---------------------------------------------------------------------------

import i18n from 'i18next'

/**
 * Format a number in compact form: 12,345 → "12.3K"; 1,234,567 → "1.2M";
 * 1,500,000,000 → "1.5B".
 * Strips trailing `.0` (so 1000 → "1K", not "1.0K").
 *
 * Returns "—" for null/undefined — several V11 BE resources (e.g. FeedPostResource
 * audience_reach) intentionally emit null when the underlying metric is missing
 * (IG photos/carousels with no view count), and the FE renders an em-dash.
 */
export function formatCompact(n: number | null | undefined): string {
  if (n == null) return '—'
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`
  return n.toString()
}

/**
 * Format the engagement-rate field (FEL-36).
 *
 * The BE computes `engagement_rate = (likes + comments + shares) / author
 * followers × 100` with 2 decimals of precision (see `discovery_content_view`
 * MV definition). For mega-accounts (NBA 90M, championsleague 121M, ...) a
 * post with hundreds of engagements rounds to `0.00` — technically correct
 * but reads as "post failed" in the UI.
 *
 * Rules:
 *   - null            → "—" (no signal)
 *   - 0 AND engagements > 0 → "<0.01%" (rounded below precision, NOT zero)
 *   - 0 AND engagements = 0 → "0%" (truly no engagement)
 *   - < 0.01 (non-zero) → "<0.01%" (same display as the rounded case)
 *   - otherwise        → e.g. "3.4%", "0.42%"
 *
 * Keeps the value column visually homogeneous (always a percentage shape),
 * avoids overloading with TOXIC's "low" sub-label (which means GOOD low,
 * the opposite tone), and preserves the analytical character of the
 * surface — "<0.01%" is the canonical notation for "below display
 * precision but not zero".
 */
export function formatEngagementRate(
  engagementRate: number | null | undefined,
  likes: number,
  comments: number,
  shares: number,
): string {
  if (engagementRate == null) return '—'
  // Uma taxa de exactamente 0 com interacções contadas significa "abaixo da
  // precisão de exibição", não "nenhuma" — daí precisar dos contadores.
  if (engagementRate === 0 && likes + comments + shares > 0) return '<0.01%'
  return formatEngagementRateValue(engagementRate)
}

/**
 * A mesma regra de exibição, para superfícies que só têm a taxa e não os
 * contadores de interacções — médias agregadas, cartões de canal, estatísticas
 * de segmento (AL-1448).
 *
 * Existe para que a precisão seja a mesma em todo o lado. Antes disto, o Feed
 * usava o `formatEngagementRate` (2 casas abaixo de 1%, 1 casa acima) e o
 * Discovery, os cartões de canal e os segmentos faziam `.toFixed(1)` inline —
 * a mesma métrica lia-se com precisões diferentes consoante o ecrã, e o
 * arredondamento a 1 casa colapsava a 0,0% valores que o servidor já tinha
 * arredondado a 2.
 */
export function formatEngagementRateValue(
  engagementRate: number | null | undefined,
): string {
  if (engagementRate == null) return '—'
  if (engagementRate === 0) return '0%'
  if (engagementRate < 0.01) return '<0.01%'
  // Drop the trailing .0 so 3.0% reads as "3%" but 0.42% stays as "0.42%".
  // 2-decimal display for sub-1% rates surfaces fine-grain differences
  // (e.g. 0.42% vs 0.05%); 1-decimal otherwise keeps the chrome quiet.
  if (engagementRate < 1) return `${engagementRate.toFixed(2)}%`
  return `${engagementRate.toFixed(1).replace(/\.0$/, '')}%`
}

// Picks the Intl locale for the app's language. Since AL-1843 the app's codes
// are already BCP 47 (`pt-BR`) and pass through; this pins the region of the
// bare ones (`en` → en-US) and still reads the backend's old codes (`br`), which
// some callers and tests hand in.
const INTL_LOCALES: Record<string, string> = {
  en: 'en-US',
  pt: 'pt-PT',
  br: 'pt-BR',
  es: 'es-ES',
}

export function toIntlLocale(language: string): string {
  return INTL_LOCALES[language] ?? language
}

/**
 * The platform's one date format (AL-1964), in the account's language:
 *   - en: month first, "Mar 3" / "Mar 3, 2026" (the topbar's "Oct 16, 2026");
 *   - pt, br, es: day first, "3 mar." / "3 mar. 2026".
 * Composed from separate parts on purpose: CLDR renders `{ day, month: 'short' }`
 * as "27/02" in pt-PT, which would lose the month name.
 *
 * `timeZone: 'UTC'` for date-only values ("2026-02-27", which must not shift a
 * day); timestamps pass `undefined` to read in the viewer's timezone.
 */
function composeDate(
  date: Date,
  language: string,
  withYear: boolean,
  timeZone: string | undefined
): string {
  const locale = toIntlLocale(language)
  const part = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(date)
  const day = part({ day: 'numeric' })
  const month = part({ month: 'short' })
  const year = part({ year: 'numeric' })
  if (locale.startsWith('en')) return withYear ? `${month} ${day}, ${year}` : `${month} ${day}`
  return withYear ? `${day} ${month} ${year}` : `${day} ${month}`
}

/** A date-only value in the platform format: "Mar 3" / "Mar 3, 2026" (en). (AL-1852, AL-1964) */
export function formatDayMonth(date: Date, language: string, withYear = false): string {
  return composeDate(date, language, withYear, 'UTC')
}

/** A timestamp's day in the viewer's timezone, in the platform format. */
export function formatLocalDay(date: Date, language: string, withYear = false): string {
  return composeDate(date, language, withYear, undefined)
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const NAIVE_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/

/**
 * A date the API sends (date-only or timestamp) in the platform format, with
 * the year: date-only values are read as UTC days, timestamps in the viewer's
 * timezone. "—" for an empty or unreadable value. (AL-1964)
 */
export function formatDateValue(value: string | null | undefined, language: string): string {
  if (!value) return '—'
  // "YYYY-MM-DD HH:mm:ss" (no zone) is how the API sends UTC timestamps; read
  // it as UTC like parseUtc does, also because Safari rejects the space form.
  const iso = DATE_ONLY.test(value)
    ? `${value}T00:00:00Z`
    : NAIVE_TIMESTAMP.test(value)
      ? `${value.replace(' ', 'T')}Z`
      : value
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return DATE_ONLY.test(value)
    ? formatDayMonth(date, language, true)
    : formatLocalDay(date, language, true)
}

/** formatDateValue in the language the app is showing, for code with no hook at hand. */
export function formatAppDate(value: string | null | undefined): string {
  return formatDateValue(value, i18n.language || 'en')
}

/** Short month + year in the account's language: "Sep 2026" (en), "set. de 2026" (pt). */
export function formatMonthYear(date: Date, language: string): string {
  return new Intl.DateTimeFormat(toIntlLocale(language), {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

const NUMBER_FORMATS = new Map<string, Intl.NumberFormat>()

// `useGrouping: 'always'` is Intl.NumberFormat v3 (Chrome 106, Safari 15.4,
// Firefox 116); the project's TS lib still types useGrouping as boolean.
const GROUPED_NUMBER_OPTIONS = {
  useGrouping: 'always',
  maximumFractionDigits: 3,
} as unknown as Intl.NumberFormatOptions

/**
 * A number written out in full in the account's language, always grouped:
 * "9,897" (en), "9.897" (br, es), "9 897" (pt). (AL-1839)
 *
 * The one formatter for counts across the platform. Before it, the same screen
 * mixed a hardcoded comma (CountUp `separator=","`), the *browser's* language
 * (bare `toLocaleString()`) and raw numbers — "71,110", "46 654" and "9897" side
 * by side. `useGrouping: 'always'` because CLDR leaves 4-digit numbers ungrouped
 * in pt-PT and es-ES ("9897"), which read as unformatted next to "10 576".
 * Fractions keep toLocaleString's default (up to 3 digits).
 */
export function formatLocaleNumber(value: number, language: string): string {
  const locale = toIntlLocale(language)
  let formatter = NUMBER_FORMATS.get(locale)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, GROUPED_NUMBER_OPTIONS)
    NUMBER_FORMATS.set(locale, formatter)
  }
  return formatter.format(value)
}

/**
 * formatLocaleNumber in the language the app is showing, for code with no
 * hook at hand (cell renderers, pure helpers). Same output as useFormatNumber:
 * always grouped, never the browser's locale (AL-1964).
 */
export function formatCount(value: number): string {
  return formatLocaleNumber(value, i18n.language || 'en')
}

type Translate = (key: string, options?: Record<string, unknown>) => string

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

// Always 24h ("17:20"), matching the pill's original look in every locale.
function formatClock(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(date)
}

function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

/**
 * Format a post's ISO timestamp for the Feed card pill, by calendar day in
 * the viewer's timezone:
 *   - today, < 1h ago: "Just now"
 *   - today: "Today · HH:MM"
 *   - yesterday: "Yesterday · HH:MM"
 *   - older: localized short date + time, e.g. "Sep 13 · 09:05"
 *
 * AL-1797: this used to count elapsed hours, so under the Feed's default
 * "Yesterday" filter a post from yesterday 17:20 read "Today · 17:20".
 */
export function formatRelativeTimestamp(
  iso: string,
  t: Translate,
  language: string,
  now: Date = new Date()
): string {
  const date = new Date(iso)
  const locale = toIntlLocale(language)
  const time = formatClock(date, locale)
  // Math.round absorbs the 23h/25h days around DST changes.
  const dayDiff = Math.round((startOfLocalDay(now) - startOfLocalDay(date)) / DAY_MS)

  if (dayDiff <= 0) {
    return now.getTime() - date.getTime() < HOUR_MS
      ? t('app.v11.feed.card.timestamp.justNow')
      : t('app.v11.feed.card.timestamp.today', { time })
  }
  if (dayDiff === 1) return t('app.v11.feed.card.timestamp.yesterday', { time })
  return `${formatLocalDay(date, language)} · ${time}`
}

/**
 * Format a post's last-update timestamp (BE `updated_at`, AL-1798) as a
 * localized day/month plus time: "15/09 · 08:12" (pt), "09/15 · 08:12" (en).
 */
export function formatUpdatedAt(iso: string, language: string): string {
  const date = new Date(iso)
  // "Oct 16 · 08:03" like every other date, not "10/16 · 08:03" (AL-1964).
  return `${formatLocalDay(date, language)} · ${formatClock(date, toIntlLocale(language))}`
}

/**
 * Format a TAV (True Audience Value) for the Discovery table cell.
 *
 * ⚠️ ALWAYS `$` AND ALWAYS en-US, deliberately — not locale-aware.
 *
 * The same client receives an Excel report where the TAV column is written as
 * `$1,190,638.23` (alley-hooper/reports/generate_channel_topic_report_v1.py:2194,
 * header literally `"TAV ($)"`). Formatting `6,06 M` here for a pt reader would
 * show two different-looking numbers for the same post across two surfaces.
 * Matching the source is worth more than matching the locale.
 *
 * If i18n ever wants this, the report has to move first — not this function.
 *
 * Rules:
 *   - null / undefined / non-finite → "—"  (no row in post_tav, or indeterminate)
 *   - 0                → "$0.00"  (a REAL zero; 1.605 posts have one)
 *   - >= ~1M           → "$6.06M"
 *   - >= 1k            → "$757.9K"
 *   - otherwise        → "$123.45"
 *   - negative         → "-$500.00"  (sign before the symbol, as Intl does)
 *
 * null and zero are NOT the same and must not render the same: the backend
 * keeps the column nullable precisely to preserve that distinction.
 */
export function formatTav(value: number | null | undefined): string {
  // `!Number.isFinite` and not just `== null`: a NaN slipping through from the
  // API would otherwise render as "$NaN" in the table.
  if (value == null || !Number.isFinite(value)) return '—'

  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)

  // 999_950 and not 1_000_000: at one decimal, 999_999 formats as "1000.0K",
  // which nobody writes. Promote to millions as soon as the K form would round
  // to 1000.
  //
  // ⚠️ DIVERGE DE PROPÓSITO do `formatCompact` acima, que mantém o mesmo caso
  // como "1000K" e o tem FIXADO em teste ("Locked intentionally"). Não é
  // descuido nem desconhecimento: uma contagem a dizer "1000K" é estranha, um
  // valor monetário a dizer "$1000.0K" é errado — ninguém escreve preços assim.
  // Se algum dia se quiser uniformizar, é o `formatCompact` que muda, e o teste
  // dele tem de ser desbloqueado em conjunto.
  if (abs >= 999_950) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`
  return `${sign}$${abs.toFixed(2)}`
}

/**
 * The same TAV at full precision, for the tooltip.
 *
 * The cell is compact so the column stays narrow; the exact figure is what
 * reconciles with the report, so it has to be reachable somewhere.
 *
 *   formatTavExact(6056146.14) // "$6,056,146.14"
 */
export function formatTavExact(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}
